"use client"

import { useCallback, useEffect, useRef, useState } from "react"

const DEFAULT_UNDO_MS = 6000

type DeferredDeleteRequest = {
  mediaId: string
  applyOptimisticRemove: () => void
  restoreOptimistic: () => void
}

type UseDeferredMediaDeleteOptions = {
  commitDelete: (mediaId: string) => Promise<void>
  /** Runs after the server confirms deletion (e.g. refetch). */
  onCommitSuccess?: () => void
  /** Optional error line (e.g. parent sets banner). */
  onCommitError?: (message: string) => void
  undoWindowMs?: number
}

/**
 * Optimistic remove + delayed DELETE + undo. One pending delete at a time.
 * Flushes commit on unmount if the undo window is still active (unless user undid).
 */
export function useDeferredMediaDelete({
  commitDelete,
  onCommitSuccess,
  onCommitError,
  undoWindowMs = DEFAULT_UNDO_MS,
}: UseDeferredMediaDeleteOptions) {
  const commitRef = useRef(commitDelete)
  const successRef = useRef<typeof onCommitSuccess>(undefined)
  const errorRef = useRef<typeof onCommitError>(undefined)
  commitRef.current = commitDelete
  successRef.current = onCommitSuccess
  errorRef.current = onCommitError

  const pendingRef = useRef<{
    timer: ReturnType<typeof setTimeout>
    undoUsed: boolean
    restore: () => void
    mediaId: string
  } | null>(null)

  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [pendingMediaId, setPendingMediaId] = useState<string | null>(null)
  const [committingMediaId, setCommittingMediaId] = useState<string | null>(
    null,
  )

  const runCommit = useCallback(async (mediaId: string, restore: () => void) => {
    setCommittingMediaId(mediaId)
    try {
      await commitRef.current(mediaId)
      successRef.current?.()
    } catch {
      restore()
      const msg = "Couldn’t delete that. Restored."
      errorRef.current?.(msg)
    } finally {
      setCommittingMediaId(null)
    }
  }, [])

  const undo = useCallback(() => {
    const p = pendingRef.current
    if (!p || p.undoUsed) return
    p.undoUsed = true
    clearTimeout(p.timer)
    pendingRef.current = null
    setSnackbarOpen(false)
    setPendingMediaId(null)
    p.restore()
  }, [])

  const requestDelete = useCallback(
    (req: DeferredDeleteRequest) => {
      const id = req.mediaId.trim()
      if (!id || pendingRef.current) return

      req.applyOptimisticRemove()
      setSnackbarOpen(true)
      setPendingMediaId(id)

      const restore = req.restoreOptimistic
      const timer = setTimeout(() => {
        const cur = pendingRef.current
        if (!cur || cur.undoUsed || cur.mediaId !== id) return
        pendingRef.current = null
        setSnackbarOpen(false)
        setPendingMediaId(null)
        void runCommit(id, restore)
      }, undoWindowMs)

      pendingRef.current = {
        timer,
        undoUsed: false,
        restore,
        mediaId: id,
      }
    },
    [runCommit, undoWindowMs],
  )

  useEffect(() => {
    return () => {
      const p = pendingRef.current
      if (!p || p.undoUsed) return
      clearTimeout(p.timer)
      const { mediaId, restore } = p
      pendingRef.current = null
      setSnackbarOpen(false)
      setPendingMediaId(null)
      void (async () => {
        setCommittingMediaId(mediaId)
        try {
          await commitRef.current(mediaId)
          successRef.current?.()
        } catch {
          restore()
          errorRef.current?.("Couldn’t delete that. Restored.")
        } finally {
          setCommittingMediaId(null)
        }
      })()
    }
  }, [])

  const blockedDeleteMediaId = pendingMediaId ?? committingMediaId

  return {
    requestDelete,
    undo,
    snackbarOpen,
    pendingMediaId,
    committingMediaId,
    /** Blocks ⋯ delete while undo window is open or API commit is in flight. */
    blockedDeleteMediaId,
  }
}
